import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Mic, Factory, Globe, GraduationCap } from 'lucide-react';
import { Button } from '../components/ui/button';

const AboutPage = () => {
  useEffect(() => {
    const timer = setTimeout(() => {
      document.title = 'The APK Advantage | APK Fintech \u2014 CA Prateek Kothari';
      let metaDesc = document.querySelector('meta[name="description"]');
      if (!metaDesc) {
        metaDesc = document.createElement('meta');
        metaDesc.name = 'description';
        document.head.appendChild(metaDesc);
      }
      metaDesc.content = 'APK Fintech is a boutique wealth management and financial consultancy practice led by CA Prateek Kothari. Serving HNI individuals, business owners, and NRI clients across India with expert advice in wealth management, NRI taxation, estate planning, and business consultancy.';
    }, 100);
    return () => {
      clearTimeout(timer);
      document.title = 'APKfintech';
    };
  }, []);

  return (
    <div className="category-page" data-testid="about-page">
      <div className="category-page-container">

        {/* Hero Section */}
        <div className="about-hero" data-testid="about-hero">
          <h1 className="about-hero-headline">Guided by Knack. Driven by Gut. Grounded in Trust.</h1>
          <p className="about-hero-subtext">A distinguished boutique wealth management and financial consultancy practice, founded by CA Prateek Kothari — serving discerning individuals, families, NRI clients, and enterprises across India.</p>
          <p className="about-hero-tagline">Where Knack Meets Conviction — and Wealth Finds Direction.</p>
        </div>

        {/* Founder Section */}
        <div className="about-founder" data-testid="about-founder">
          <div className="about-founder-info">
            <span className="about-founder-label">Meet the Founder</span>
            <h2 className="about-founder-name">CA Prateek Kothari</h2>
            <p className="about-founder-designation">Founder & Principal Advisor</p>
            <div className="about-founder-credentials">
              <span>Chartered Accountant</span>
              <span className="about-cred-sep">&middot;</span>
              <span>Wealth Manager</span>
              <span className="about-cred-sep">&middot;</span>
              <span>Financial Consultant</span>
            </div>
            <div className="about-founder-bio">
              <p>CA Prateek Kothari is a Chartered Accountant with over 15 years of experience spanning wealth advisory, portfolio management, NRI taxation, estate planning, and financial consultancy for businesses and individuals across India.</p>
              <p>He leads with knack and gut feeling — placing each client's long-term financial wellbeing above all else. His advice is shaped not by product incentives but by a genuine commitment to transformative, relationship-driven counsel.</p>
              <p>Beyond client advisory, Prateek is a respected voice at professional forums, industry conferences, and academic institutions — contributing actively to financial literacy and the elevation of advisory standards across the profession.</p>
            </div>
            <div className="about-founder-details">
              <div className="about-founder-detail">
                <span className="about-detail-label">Experience</span>
                <span className="about-detail-value">15+ years</span>
              </div>
              <div className="about-founder-detail">
                <span className="about-detail-label">Reach</span>
                <span className="about-detail-value">Pan India & NRI Clients</span>
              </div>
              <div className="about-founder-detail">
                <span className="about-detail-label">Practice Areas</span>
                <span className="about-detail-value">Wealth Management | Financial Consultancy</span>
              </div>
            </div>
          </div>
          <div className="about-founder-stats">
            <div className="about-stat-block">
              <span className="about-stat-value">15+</span>
              <span className="about-stat-label">Years Experience</span>
            </div>
            <div className="about-stat-block">
              <span className="about-stat-value">Pan India</span>
              <span className="about-stat-label">& NRI Reach</span>
            </div>
            <div className="about-stat-block">
              <span className="about-stat-value">2</span>
              <span className="about-stat-label">Practice Disciplines</span>
            </div>
          </div>
        </div>

        {/* Voice That Shapes the Profession */}
        <div className="about-voice-section" data-testid="about-voice">
          <h2 className="about-section-heading">A Voice That Shapes the Profession</h2>
          <p className="about-section-intro">CA Prateek Kothari is not only an advisor to his clients — he is an active contributor to India's broader financial and professional community, sharing expertise across some of the country's most respected platforms, institutions, and industry bodies.</p>
          <div className="about-voice-grid">
            <div className="about-voice-tile" data-testid="voice-tile-0">
              <div className="about-voice-icon"><Mic size={24} /></div>
              <h4 className="about-voice-title">ICAI Speaker</h4>
              <p className="about-voice-body">Featured speaker at Institute of Chartered Accountants of India (ICAI) events, addressing practitioners on personal finance, tax-efficient wealth structuring, and investment planning frameworks.</p>
              <span className="about-voice-tag">Professional Association</span>
            </div>
            <div className="about-voice-tile" data-testid="voice-tile-1">
              <div className="about-voice-icon"><Factory size={24} /></div>
              <h4 className="about-voice-title">CII Forums</h4>
              <p className="about-voice-body">Invited speaker at Confederation of Indian Industry (CII) conferences, contributing expertise on financial advisory best practices, investment strategy, and wealth management in India.</p>
              <span className="about-voice-tag">Industry Body</span>
            </div>
            <div className="about-voice-tile" data-testid="voice-tile-2">
              <div className="about-voice-icon"><Globe size={24} /></div>
              <h4 className="about-voice-title">Rotary & Round Table India</h4>
              <p className="about-voice-body">Regular speaker at Rotary Club and Round Table India events — conducting sessions on personal finance, estate planning, and retirement strategy for business owners and community leaders.</p>
              <span className="about-voice-tag">Community Leadership</span>
            </div>
            <div className="about-voice-tile" data-testid="voice-tile-3">
              <div className="about-voice-icon"><GraduationCap size={24} /></div>
              <h4 className="about-voice-title">University & Academic Events</h4>
              <p className="about-voice-body">Invited to university campuses to speak on financial planning, investment awareness, and career pathways in chartered accountancy — inspiring the next generation of finance professionals.</p>
              <span className="about-voice-tag">Academic Outreach</span>
            </div>
            <div className="about-voice-tile about-voice-tile-wide" data-testid="voice-tile-4">
              <div className="about-voice-icon"><Globe size={24} /></div>
              <h4 className="about-voice-title">NRI Taxation Specialist</h4>
              <p className="about-voice-body">Recognised for deep expertise in NRI taxation, FEMA compliance, and cross-border financial planning — advising the Indian diaspora across USA, UK, Middle East, Australia and Southeast Asia.</p>
            </div>
          </div>
          <div className="about-topics-bar" data-testid="about-topics">
            <span className="about-topics-label">Key Speaking Topics</span>
            <div className="about-topics-pills">
              <span className="about-topic-pill">Personal Finance & Financial Planning</span>
              <span className="about-topic-pill">Estate Planning & Succession</span>
              <span className="about-topic-pill">NRI Taxation & Cross-Border Advisory</span>
              <span className="about-topic-pill">Wealth Structuring & Investment Strategy</span>
              <span className="about-topic-pill">Tax Planning for Businesses & Individuals</span>
            </div>
          </div>
        </div>

        {/* Mission & Vision */}
        <div className="about-mv-section" data-testid="about-mission-vision">
          <span className="about-mv-label">Purpose & Direction</span>
          <h2 className="about-section-heading">Our Mission & Vision</h2>
          <p className="about-section-intro">At APK Fintech, every engagement, every recommendation, and every client relationship is anchored in a clear sense of purpose — and a long-term vision for the role we play in the financial lives of those we serve.</p>
          <div className="about-mv-grid">
            <div className="about-mv-card about-mv-mission" data-testid="about-mission">
              <span className="about-mv-card-label">Our Mission</span>
              <p className="about-mv-statement">To empower individuals, families, NRI clients, and enterprises across India to achieve enduring financial prosperity — by delivering independent, integrity-driven wealth management and consultancy, guided by professional rigour and the instinctive insight of a truly trusted advisor.</p>
              <p className="about-mv-note">We exist to translate financial complexity into clarity, and aspiration into strategy — one client, one relationship at a time.</p>
            </div>
            <div className="about-mv-card about-mv-vision" data-testid="about-vision">
              <span className="about-mv-card-label">Our Vision</span>
              <p className="about-mv-statement">To be India's most trusted boutique financial advisory practice — where the precision of chartered accountancy and the art of relationship-driven wealth management converge to create financial legacies that endure across generations.</p>
              <p className="about-mv-note">A firm where clients return not because of products, but because of the trust, conviction, and clarity we bring to every conversation.</p>
            </div>
          </div>
        </div>

        {/* Core Values */}
        <div className="about-values-section" data-testid="about-values">
          <span className="about-mv-label">What We Stand For</span>
          <h2 className="about-section-heading">The Principles at the Core of Everything We Do</h2>
          <div className="inh-advantage-list">
            <div className="inh-advantage-block" data-testid="value-0">
              <span className="inh-advantage-number">01</span>
              <div className="inh-advantage-content">
                <h4 className="inh-advantage-title">Integrity First</h4>
                <p className="inh-advantage-body">Our advice is never shaped by product commissions or institutional incentives. Our sole obligation is to the financial wellbeing of our clients.</p>
              </div>
            </div>
            <div className="inh-advantage-block" data-testid="value-1">
              <span className="inh-advantage-number">02</span>
              <div className="inh-advantage-content">
                <h4 className="inh-advantage-title">Knack & Conviction</h4>
                <p className="inh-advantage-body">Great advice requires instinct — the knack to see what numbers alone cannot reveal, and the conviction to act on it decisively.</p>
              </div>
            </div>
            <div className="inh-advantage-block" data-testid="value-2">
              <span className="inh-advantage-number">03</span>
              <div className="inh-advantage-content">
                <h4 className="inh-advantage-title">Deep Expertise</h4>
                <p className="inh-advantage-body">Rooted in the rigour of chartered accountancy, we bring technical depth across taxation, investments, and financial law that very few independent advisors in India can match.</p>
              </div>
            </div>
            <div className="inh-advantage-block" data-testid="value-3">
              <span className="inh-advantage-number">04</span>
              <div className="inh-advantage-content">
                <h4 className="inh-advantage-title">Long-Term Thinking</h4>
                <p className="inh-advantage-body">We are oriented toward enduring outcomes — not quarterly performance. Our strategies are designed to withstand market cycles, life events, and the full test of time.</p>
              </div>
            </div>
            <div className="inh-advantage-block" data-testid="value-4">
              <span className="inh-advantage-number">05</span>
              <div className="inh-advantage-content">
                <h4 className="inh-advantage-title">Transparency</h4>
                <p className="inh-advantage-body">Clients always know what we are doing and why. From fee structures to investment rationale — clarity is a fundamental expression of respect.</p>
              </div>
            </div>
            <div className="inh-advantage-block" data-testid="value-5">
              <span className="inh-advantage-number">06</span>
              <div className="inh-advantage-content">
                <h4 className="inh-advantage-title">Absolute Confidentiality</h4>
                <p className="inh-advantage-body">The trust our clients place in us is sacred. We maintain complete discretion across every engagement, conversation, and financial detail entrusted to us.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Clients We Serve */}
        <div className="about-topics-bar" data-testid="about-clients">
          <span className="about-topics-label">Clients We Serve</span>
          <div className="about-topics-pills">
            <span className="about-topic-pill">Pan India</span>
            <span className="about-topic-pill">NRI — USA & Canada</span>
            <span className="about-topic-pill">NRI — UK & Europe</span>
            <span className="about-topic-pill">NRI — Middle East</span>
            <span className="about-topic-pill">NRI — Australia & Southeast Asia</span>
            <span className="about-topic-pill">HNI & Ultra HNI Families</span>
            <span className="about-topic-pill">Business Owners & Entrepreneurs</span>
            <span className="about-topic-pill">Corporates & Institutions</span>
          </div>
        </div>

      </div>

      {/* Final CTA */}
      <div className="about-final-cta" data-testid="about-final-cta">
        <div className="about-final-cta-inner">
          <span className="about-final-label">Let's Begin</span>
          <h2 className="about-final-headline">Trusted Counsel for the Decisions That Shape Your Financial Future</h2>
          <p className="about-final-tagline">Where Knack Meets Conviction — and Wealth Finds Direction.</p>
          <a href="mailto:prateek@apkfintech.com">
            <Button className="btn-primary about-final-btn" data-testid="about-cta-btn">
              Schedule a Conversation <ArrowRight size={20} />
            </Button>
          </a>
          <p className="about-final-website">www.apkfintech.com</p>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;
